import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  useQuery,
  useMutation,
  useLazyQuery,
  useApolloClient,
  gql,
} from "@apollo/client";
import usePusherSubscription from "../../../hooks/usePusherSubscription";
import {
  Typography,
  Box,
  Popover,
  Tooltip,
  Avatar,
  CircularProgress,
  Fade,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Collapse,
  Chip,
  Select,
  MenuItem,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import ArchiveIcon from "@mui/icons-material/Archive";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MicIcon from "@mui/icons-material/Mic";

// Import modern styled components
import {
  StyledMainContainer,
  StyledAgentStatusBar,
  StyledContentArea,
  StyledSidebar,
  StyledSessionList,
  StyledSessionCard,
  StyledBadge,
  StyledButton,
  StyledAcceptButton,
  StyledDeclineButton,
  StyledChatArea,
  StyledChatHeader,
  StyledMessagesContainer,
  StyledMessageBubble,
  StyledInputArea,
  StyledInputWrapper,
  StyledInput,
  StyledSendButton,
  StyledNotificationPopover,
  StyledNotificationItem,
  StyledActionButtons,
  StyledActionIconButton,
  StyledChip,
  StyledStatusDot,
  StyledEmptyState,
  StyledSkeleton,
  StyledBotSummary,
  StyledTabButton,
  StyledAcceptBanner,
  theme,
} from "./ModernStyledComponents";

import CoBrowsingView from "./CoBrowsingView";
import MiroTalkVideoCallPopup from "./MiroTalkVideoCallPopup";
import {
  GET_VISITOR_BY_USER_ID,
  UPDATE_VISITOR_INFO,
} from "./graphql/visitorQueries";

// GraphQL Queries and Mutations
const START_CO_BROWSE = gql`
  mutation StartCoBrowse($chatSessionId: ID!) {
    startCoBrowse(chatSessionId: $chatSessionId) {
      id
      chatSessionId
      status
      createdAt
    }
  }
`;

const SEND_CO_BROWSE_EVENT = gql`
  mutation SendCoBrowseEvent($sessionId: ID!, $type: String!, $data: JSON!) {
    sendCoBrowseEvent(sessionId: $sessionId, type: $type, data: $data)
  }
`;

const END_CHAT_SESSION = gql`
  mutation EndChatSession($input: EndChatSessionInput!) {
    endChatSession(input: $input) {
      id
      status
    }
  }
`;

const GET_CURRENT_USER = gql`
  query GetCurrentUser($id: ID!) {
    getUser(id: $id) {
      id
      firstName
      lastName
      email
      client {
        id
        name
        companyName
      }
    }
  }
`;

const GET_PENDING_SESSIONS = gql`
  query GetPendingChatSessions {
    getPendingChatSessions {
      id
      userId
      visitorSessionId
      status
      agentId
      createdAt
      updatedAt
      agentRequestedAt
      endedAt
      isArchived
      metadata
    }
  }
`;

// GraphQL subscription definitions removed - now using Pusher via usePusherSubscription

const ACCEPT_CHAT_SESSION = gql`
  mutation AcceptChatSession($sessionId: ID!) {
    acceptChatSession(sessionId: $sessionId) {
      success
      chatSession {
        id
        status
        agentId
      }
    }
  }
`;

const DECLINE_CHAT_SESSION = gql`
  mutation DeclineChatSession($sessionId: ID!) {
    declineChatSession(sessionId: $sessionId)
  }
`;

const SEND_CHAT_MESSAGE = gql`
  mutation SendChatMessage(
    $sessionId: ID!
    $sender: String!
    $content: String!
    $senderData: JSON
  ) {
    sendRealtimeChatMessage(
      sessionId: $sessionId
      sender: $sender
      content: $content
      senderData: $senderData
    ) {
      id
      sender
      content
      timestamp
      senderData
    }
  }
`;

const GET_CHAT_MESSAGES = gql`
  query GetChatMessages($sessionId: ID!) {
    getChatMessages(sessionId: $sessionId) {
      id
      sender
      content
      timestamp
      senderData
    }
  }
`;

// MESSAGE_SUBSCRIPTION and MESSAGE_SENT removed - now using Pusher

const CLEAR_CHAT_SESSIONS = gql`
  mutation ClearChatSessions {
    clearChatSessions {
      success
      sessionsDeleted
      messagesDeleted
      error
    }
  }
`;

const ARCHIVE_CHAT_SESSION = gql`
  mutation ArchiveChatSession($sessionId: ID!) {
    archiveChatSession(sessionId: $sessionId) {
      id
      status
      isArchived
    }
  }
`;

// CHAT_SESSION_UPDATED subscription removed - now using Pusher

// Helper functions
function formatTime(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return "Now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatFullTime(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function IncomingRequests() {
  // State
  // Initialize as null - session will be restored by useEffect when GET_PENDING_SESSIONS completes
  // This ensures messages and header buttons appear together (both require full session object)
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [videoCallActive, setVideoCallActive] = useState(false);
  const [callType, setCallType] = useState(null); // 'audio' or 'video'
  const [isCoBrowsing, setIsCoBrowsing] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(new Map());
  const [visitorInfo, setVisitorInfo] = useState(null);
  const [editVisitorDialogOpen, setEditVisitorDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showBotSummary, setShowBotSummary] = useState(true);
  const [agentStatus, setAgentStatus] = useState("online");
  const [acceptedSessionIds, setAcceptedSessionIds] = useState(new Set()); // Track sessions accepted by this agent to prevent stale UI
  const acceptedSessionIdsRef = useRef(acceptedSessionIds); // Ref to avoid stale closures in subscription handlers
  const chatContainerRef = useRef(null);
  const previousMessagesRef = useRef([]);

  // Keep ref in sync with state so subscription callbacks always see latest value
  useEffect(() => {
    acceptedSessionIdsRef.current = acceptedSessionIds;
  }, [acceptedSessionIds]);

  // Extract clientId from JWT for Pusher channel subscriptions
  const clientId = useMemo(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.clientId || payload.client || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }, []);

  const apolloClient = useApolloClient();

  // GraphQL mutations
  const [startCoBrowse] = useMutation(START_CO_BROWSE);
  const [sendCoBrowseEvent] = useMutation(SEND_CO_BROWSE_EVENT);
  const [acceptSession] = useMutation(ACCEPT_CHAT_SESSION);
  const [declineSession] = useMutation(DECLINE_CHAT_SESSION);
  const [sendMessage] = useMutation(SEND_CHAT_MESSAGE);
  const [endChatSession] = useMutation(END_CHAT_SESSION);
  const [clearChatSessions] = useMutation(CLEAR_CHAT_SESSIONS);
  const [archiveChatSession] = useMutation(ARCHIVE_CHAT_SESSION);

  // Visitor info queries
  const [getVisitorByUserId] = useLazyQuery(GET_VISITOR_BY_USER_ID, {
    onCompleted: (data) => setVisitorInfo(data?.getVisitorByUserId),
    onError: () => setVisitorInfo(null),
  });

  const [updateVisitorInfo] = useMutation(UPDATE_VISITOR_INFO, {
    onCompleted: (data) => {
      setVisitorInfo(data?.updateVisitorInfo);
      setEditVisitorDialogOpen(false);
    },
  });

  // Handle session selection
  const handleSessionSelect = useCallback(
    (session) => {
      setUnreadMessages((prev) => {
        const newMap = new Map(prev);
        newMap.delete(session.id);
        return newMap;
      });

      // Get the latest version of this session from pendingSessions
      const latestSession =
        pendingSessions.find((s) => s.id === session.id) || session;
      setSelectedSession(latestSession);

      // Don't clear messages here - let the useEffect handle it when new data arrives
      // The useEffect will replace messages when messagesData updates
      // This prevents the flash of empty chat while waiting for new data
      previousMessagesRef.current = [];

      // Clear other session-specific state
      setMessageInput("");
      setVideoCallActive(false);
      setIsCoBrowsing(false);
      setVisitorInfo(null);

      localStorage.setItem(
        "instaconnect_selected_session_id",
        latestSession.id
      );
      const visitorId =
        latestSession?.visitorSessionId || latestSession?.userId;
      if (visitorId) {
        getVisitorByUserId({ variables: { userId: visitorId } });
      }
    },
    [getVisitorByUserId, pendingSessions]
  );

  // Fetch messages
  // NOTE: Removed pollInterval - we rely on WebSocket subscriptions for real-time updates
  // Polling was causing unnecessary server load (getChatMessages called every 3s)
  const {
    data: messagesData,
    loading: loadingMessages,
    refetch: refetchMessages,
  } = useQuery(GET_CHAT_MESSAGES, {
    variables: { sessionId: selectedSession?.id },
    skip: !selectedSession,
    fetchPolicy: "cache-and-network",
    // pollInterval removed - MESSAGE_SUBSCRIPTION and MESSAGE_SENT handle real-time updates
  });

  // Update messages
  useEffect(() => {
    if (messagesData?.getChatMessages) {
      const newMessages = messagesData.getChatMessages;
      if (
        JSON.stringify(newMessages) !==
        JSON.stringify(previousMessagesRef.current)
      ) {
        setMessages(newMessages);
        previousMessagesRef.current = newMessages;
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } else if (selectedSession && !loadingMessages) {
      // Session changed but no messages yet (empty conversation) - clear old messages
      setMessages([]);
      previousMessagesRef.current = [];
    }
  }, [messagesData, selectedSession?.id, loadingMessages]);

  // Message subscription via Pusher
  usePusherSubscription(
    selectedSession?.id ? `private-session-${selectedSession.id}` : null,
    "realtime-message-sent",
    {
      skip: !selectedSession,
      onData: (newMessage) => {
        if (newMessage) {
          setMessages((prev) => {
            if (!prev.find((m) => m.id === newMessage.id)) {
              return [...prev, newMessage];
            }
            return prev;
          });
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop =
                chatContainerRef.current.scrollHeight;
            }
          }, 100);
        }
      },
    }
  );

  // Multi-session message handling
  const handleMultiSessionMessage = useCallback(
    (newMessage) => {
      const widgetEvents = [
        "Event: widget_opened",
        "Event: widget_closed",
        "Event: widget_minimized",
        "Event: chat_session_initialized",
      ];
      if (widgetEvents.includes(newMessage.content?.trim())) return;

      if (selectedSession && newMessage.sessionId === selectedSession.id) {
        setMessages((prev) => {
          if (prev.some((msg) => msg.id === newMessage.id)) return prev;
          return [...prev, newMessage].sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );
        });
      } else if (newMessage.sender === "user") {
        setUnreadMessages((prev) => {
          const currentCount = prev.get(newMessage.sessionId) || 0;
          const newMap = new Map(prev);
          newMap.set(newMessage.sessionId, currentCount + 1);
          return newMap;
        });
      }
    },
    [selectedSession]
  );

  // Multi-session Pusher subscriptions for top 3 pending sessions
  usePusherSubscription(
    pendingSessions[0]?.id ? `private-session-${pendingSessions[0].id}` : null,
    "realtime-message-sent",
    {
      skip: !pendingSessions[0]?.id,
      onData: (msg) => handleMultiSessionMessage(msg),
    }
  );

  usePusherSubscription(
    pendingSessions[1]?.id ? `private-session-${pendingSessions[1].id}` : null,
    "realtime-message-sent",
    {
      skip: !pendingSessions[1]?.id,
      onData: (msg) => handleMultiSessionMessage(msg),
    }
  );

  usePusherSubscription(
    pendingSessions[2]?.id ? `private-session-${pendingSessions[2].id}` : null,
    "realtime-message-sent",
    {
      skip: !pendingSessions[2]?.id,
      onData: (msg) => handleMultiSessionMessage(msg),
    }
  );

  // Handle accept/decline
  const handleAccept = async (session) => {
    // Immediately mark as accepted to prevent race with subscription updates.
    // The CHAT_SESSION_UPDATED subscription can fire BEFORE the await below returns,
    // which would filter out the session and clear selectedSession without this guard.
    setAcceptedSessionIds((prev) => new Set([...prev, session.id]));
    // Persist selected session so it survives page refresh and polling cycles
    localStorage.setItem("instaconnect_selected_session_id", session.id);

    try {
      const result = await acceptSession({
        variables: { sessionId: session.id },
      });
      const acceptResult = result.data?.acceptChatSession;

      // Check for expected response structure
      if (acceptResult?.success && acceptResult.chatSession) {
        // MERGE backend response with existing session to preserve all fields
        const updatedSession = {
          ...session, // Keep all original fields
          ...acceptResult.chatSession, // Override with backend response
          status: "active", // ALWAYS set to active on successful accept
        };
        setSelectedSession(updatedSession);
        setPendingSessions((prev) =>
          prev.map((s) => (s.id === session.id ? updatedSession : s))
        );
        // If filter is "pending" and we just accepted, switch to "all" so session stays visible
        if (activeFilter === "pending") {
          setActiveFilter("all");
        }
      } else {
        // FALLBACK: Backend returned unexpected structure
        // Optimistically set status to active anyway
        console.warn(
          "Accept response missing expected structure:",
          acceptResult
        );
        const updatedSession = {
          ...session,
          status: "active",
          agentId: acceptResult?.chatSession?.agentId || session.agentId,
        };
        setSelectedSession(updatedSession);
        setPendingSessions((prev) =>
          prev.map((s) => (s.id === session.id ? updatedSession : s))
        );
        if (activeFilter === "pending") {
          setActiveFilter("all");
        }
      }
    } catch (error) {
      console.error("Error accepting session:", error);
      // FALLBACK on error: Still try to update UI optimistically
      // This handles cases where backend partially succeeded or queue wasn't used
      const updatedSession = {
        ...session,
        status: "active",
      };
      setSelectedSession(updatedSession);
      setPendingSessions((prev) =>
        prev.map((s) => (s.id === session.id ? updatedSession : s))
      );
      if (activeFilter === "pending") {
        setActiveFilter("all");
      }
    }
  };

  const handleDecline = async (session) => {
    try {
      const result = await declineSession({
        variables: { sessionId: session.id },
      });
      if (result.data?.declineChatSession) {
        setPendingSessions((prev) => prev.filter((s) => s.id !== session.id));
        if (selectedSession?.id === session.id) {
          setSelectedSession(null);
          localStorage.removeItem("instaconnect_selected_session_id");
        }
      }
    } catch (error) {
      console.error("Error declining session:", error);
    }
  };

  const handleEndChat = async () => {
    if (!selectedSession) return;
    try {
      const result = await endChatSession({
        variables: { input: { chatSessionId: selectedSession.id } },
      });
      await sendMessage({
        variables: {
          sessionId: selectedSession.id,
          sender: "system",
          content: "This chat session has been ended by the support team.",
        },
      });
      setPendingSessions((prev) =>
        prev.map((s) =>
          s.id === selectedSession.id
            ? { ...s, status: result.data?.endChatSession?.status || "ended" }
            : s
        )
      );
      setSelectedSession({
        ...selectedSession,
        status: result.data?.endChatSession?.status || "ended",
      });
    } catch (error) {
      console.error("Error ending chat:", error);
    }
  };

  // Co-browsing handlers
  const handleStartCoBrowsing = async () => {
    if (!selectedSession) return;
    try {
      await startCoBrowse({ variables: { chatSessionId: selectedSession.id } });
      await sendMessage({
        variables: {
          sessionId: selectedSession.id,
          sender: "agent",
          content: `I'd like to start co-browsing to help you better. This will allow me to see your screen. Please accept or decline the request.`,
          senderData: {
            agentId: "current-agent",
            messageType: "co_browse_request",
          },
        },
      });
      await sendCoBrowseEvent({
        variables: {
          sessionId: selectedSession.id,
          type: "co_browse_start",
          data: { agentId: "current-agent" },
        },
      });
      setIsCoBrowsing(true);
    } catch (error) {
      console.error("Error starting co-browsing:", error);
    }
  };

  // Video/Audio call handlers
  const startCall = async (type) => {
    if (!selectedSession || videoCallActive) return;
    setCallType(type);
    setVideoCallActive(true);

    const callTypeText = type === "audio" ? "audio" : "video";
    await sendMessage({
      variables: {
        sessionId: selectedSession.id,
        sender: "agent",
        content: `I've started a ${callTypeText} call. The call will connect automatically.`,
        senderData: {
          agentId: "current-agent",
          messageType: `${type}_call_request`,
        },
      },
    });
  };

  // Send message
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedSession) return;
    const messageContent = messageInput.trim();
    setMessageInput("");
    try {
      await sendMessage({
        variables: {
          sessionId: selectedSession.id,
          sender: "agent",
          content: messageContent,
          senderData: { agentId: "current-agent" },
        },
      });
      refetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
      setMessageInput(messageContent);
    }
  };

  // Get current user ID (moved before polling useEffect so it's available there)
  const getCurrentUserId = () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.userId || payload.id || payload.sub || payload.clientId;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const currentUserId = getCurrentUserId();

  // Fetch sessions
  const { loading, error, data, refetch } = useQuery(GET_PENDING_SESSIONS, {
    pollInterval: 5000,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.getPendingChatSessions) {
      const nonArchivedSessions = data.getPendingChatSessions.filter(
        (s) => !s.isArchived
      );
      console.log(
        `🔄 [Poll] Received ${nonArchivedSessions.length} sessions, ` +
          `selectedSession=${selectedSession?.id}, ` +
          `storedId=${localStorage.getItem(
            "instaconnect_selected_session_id"
          )}, ` +
          `acceptedIds=[${[...acceptedSessionIds].join(",")}]`
      );

      // MERGE instead of replace: preserve locally-accepted sessions
      setPendingSessions((prev) => {
        const backendMap = new Map(nonArchivedSessions.map((s) => [s.id, s]));

        // Keep locally-accepted sessions that the backend didn't return
        // (protects against agentId type mismatch or query timing issues)
        const preservedSessions = prev.filter(
          (s) =>
            acceptedSessionIdsRef.current.has(s.id) && !backendMap.has(s.id)
        );

        if (preservedSessions.length > 0) {
          console.log(
            `🛡️ [Poll] Preserving ${preservedSessions.length} locally-accepted sessions not in backend response:`,
            preservedSessions.map((s) => `${s.id}(${s.status})`).join(", ")
          );
        }

        return [...nonArchivedSessions, ...preservedSessions];
      });

      // Auto-detect sessions already accepted by this agent (e.g. after page refresh)
      const myActiveSessions = nonArchivedSessions.filter(
        (s) =>
          s.agentId === currentUserId &&
          (s.status === "active" || s.status === "ended")
      );
      if (myActiveSessions.length > 0) {
        setAcceptedSessionIds((prev) => {
          const newSet = new Set(prev);
          let changed = false;
          myActiveSessions.forEach((s) => {
            if (!newSet.has(s.id)) {
              newSet.add(s.id);
              changed = true;
            }
          });
          return changed ? newSet : prev;
        });
      }

      const storedSessionId = localStorage.getItem(
        "instaconnect_selected_session_id"
      );

      if (
        storedSessionId &&
        (!selectedSession || selectedSession.id !== storedSessionId)
      ) {
        // Look in ALL non-archived sessions from backend
        const storedSession = nonArchivedSessions.find(
          (s) => s.id === storedSessionId
        );

        if (storedSession) {
          // Session found - restore it regardless of status/agentId
          // The agent was viewing this session before refresh
          console.log(
            `Restoring session ${storedSessionId} after page refresh`
          );
          handleSessionSelect(storedSession);
        } else {
          // Session not in non-archived list, check if it exists at all in the raw data
          const sessionInRawData = data.getPendingChatSessions.find(
            (s) => s.id === storedSessionId
          );
          if (sessionInRawData) {
            // Session exists but is archived - still restore for viewing
            console.log(
              `Restoring archived session ${storedSessionId} after page refresh`
            );
            handleSessionSelect(sessionInRawData);
          } else {
            // Session not in backend response — only clear if not recently accepted
            if (!acceptedSessionIds.has(storedSessionId)) {
              console.warn(
                `⚠️ [Polling] Session ${storedSessionId} not in backend response, clearing`
              );
              localStorage.removeItem("instaconnect_selected_session_id");
              setSelectedSession(null);
            } else {
              console.log(
                `🛡️ [Polling] Session ${storedSessionId} not in backend but in acceptedSessionIds, keeping`
              );
            }
          }
        }
      }
    }
  }, [data]);

  // Keep selectedSession in sync with pendingSessions updates
  useEffect(() => {
    if (selectedSession && pendingSessions.length > 0) {
      const updatedSession = pendingSessions.find(
        (s) => s.id === selectedSession.id
      );
      if (updatedSession && updatedSession.status !== selectedSession.status) {
        // Don't downgrade an accepted session from active to pending
        // (protects against stale polling data)
        if (
          acceptedSessionIdsRef.current.has(selectedSession.id) &&
          selectedSession.status === "active" &&
          (updatedSession.status === "pending" ||
            updatedSession.status === "pending_agent")
        ) {
          console.log(
            `🛡️ [Sync] Blocking status downgrade for accepted session ${selectedSession.id}: ` +
              `${selectedSession.status} → ${updatedSession.status} (stale poll data)`
          );
          return;
        }
        setSelectedSession(updatedSession);
      }
    }
  }, [pendingSessions]);

  // Session subscriptions via Pusher - client-level events
  usePusherSubscription(
    clientId ? `private-client-${clientId}` : null,
    "session-created",
    {
      skip: !clientId,
      onData: (newSession) => {
        if (newSession?.status === "pending") {
          setPendingSessions((prev) => {
            const filtered = prev.filter((s) => s.id !== newSession.id);
            return [newSession, ...filtered];
          });
          const audio = new Audio("/notification.mp3");
          audio.play().catch(() => {});
        }
      },
    }
  );

  usePusherSubscription(
    clientId ? `private-client-${clientId}` : null,
    "realtime-session-created",
    {
      skip: !clientId,
      onData: (newSession) => {
        if (
          newSession?.agentRequestedAt &&
          newSession.status === "pending_agent"
        ) {
          setPendingSessions((prev) => {
            const filtered = prev.filter((s) => s.id !== newSession.id);
            return [newSession, ...filtered];
          });
          const audio = new Audio("/notification.mp3");
          audio.play().catch(() => {});
        }
      },
    }
  );

  usePusherSubscription(
    clientId ? `private-client-${clientId}` : null,
    "session-status-changed",
    {
      skip: !clientId,
      onData: (statusChange) => {
        if (statusChange) {
          const token = localStorage.getItem("accessToken");
          let currentUserId = null;
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split(".")[1]));
              currentUserId =
                payload.userId || payload.id || payload.sub || payload.clientId;
            } catch (e) {}
          }
          // Only clear session if ANOTHER agent took it (not the current agent)
          if (
            statusChange.status === "active" &&
            statusChange.agentId &&
            String(statusChange.agentId) !== String(currentUserId) &&
            !acceptedSessionIdsRef.current.has(statusChange.sessionId)
          ) {
            setPendingSessions((prev) =>
              prev.filter((s) => s.id !== statusChange.sessionId)
            );
            if (selectedSession?.id === statusChange.sessionId) {
              setSelectedSession(null);
              localStorage.removeItem("instaconnect_selected_session_id");
            }
          }
        }
      },
    }
  );

  // Per-session update subscription via Pusher
  usePusherSubscription(
    selectedSession?.id ? `private-session-${selectedSession.id}` : null,
    "session-updated",
    {
      skip: !selectedSession?.id,
      onData: (updatedSession) => {
        if (updatedSession) {
          setPendingSessions((prev) =>
            prev.map((s) =>
              s.id === updatedSession.id ? { ...s, ...updatedSession } : s
            )
          );
          if (selectedSession?.id === updatedSession.id) {
            setSelectedSession((prev) => ({ ...prev, ...updatedSession }));
          }
        }
      },
    }
  );

  // Filter sessions
  const filteredSessions = useMemo(() => {
    let result = pendingSessions.filter(
      (s) =>
        s.status === "pending" ||
        s.status === "pending_agent" ||
        (s.status === "active" && s.agentId === currentUserId) ||
        (s.status === "ended" && s.agentId === currentUserId) ||
        acceptedSessionIds.has(s.id) // Always include sessions accepted by this agent
    );

    if (activeFilter === "active") {
      result = result.filter(
        (s) => s.status === "active" || acceptedSessionIds.has(s.id)
      );
    } else if (activeFilter === "pending") {
      result = result.filter(
        (s) => s.status === "pending" || s.status === "pending_agent"
      );
    } else if (activeFilter === "ended") {
      result = result.filter((s) => s.status === "ended");
    }

    return result.sort((a, b) => {
      if (a.status === "active" && b.status !== "active") return -1;
      if (b.status === "active" && a.status !== "active") return 1;
      if (
        (a.status === "pending" || a.status === "pending_agent") &&
        b.status !== "pending" &&
        b.status !== "pending_agent"
      )
        return -1;
      if (
        (b.status === "pending" || b.status === "pending_agent") &&
        a.status !== "pending" &&
        a.status !== "pending_agent"
      )
        return 1;
      return (
        new Date(b.updatedAt || b.createdAt) -
        new Date(a.updatedAt || a.createdAt)
      );
    });
  }, [pendingSessions, activeFilter, currentUserId, acceptedSessionIds]);

  // Clear selectedSession when it's no longer in the visible filtered list
  useEffect(() => {
    if (selectedSession) {
      const sessionInList = filteredSessions.find(
        (s) => s.id === selectedSession.id
      );
      // Don't clear if this session was just accepted by this agent (use ref to avoid stale closure)
      if (
        !sessionInList &&
        !acceptedSessionIdsRef.current.has(selectedSession.id)
      ) {
        console.warn(
          `⚠️ Clearing selected session ${selectedSession.id} — not in filtered list and not in acceptedSessionIds`
        );
        setSelectedSession(null);
        localStorage.removeItem("instaconnect_selected_session_id");
      }
    }
  }, [filteredSessions, selectedSession?.id, acceptedSessionIds]);

  // Count calculations
  const counts = useMemo(
    () => ({
      all: pendingSessions.filter(
        (s) =>
          s.status === "pending" ||
          s.status === "pending_agent" ||
          (s.status === "active" && s.agentId === currentUserId) ||
          (s.status === "ended" && s.agentId === currentUserId)
      ).length,
      active: pendingSessions.filter(
        (s) => s.status === "active" && s.agentId === currentUserId
      ).length,
      pending: pendingSessions.filter(
        (s) => s.status === "pending" || s.status === "pending_agent"
      ).length,
      ended: pendingSessions.filter(
        (s) => s.status === "ended" && s.agentId === currentUserId
      ).length,
    }),
    [pendingSessions, currentUserId]
  );

  const totalUnread = useMemo(
    () =>
      Array.from(unreadMessages.values()).reduce(
        (sum, count) => sum + count,
        0
      ),
    [unreadMessages]
  );

  const statusColors = {
    online: theme.success,
    busy: theme.warning,
    away: "#fbbf24",
    offline: theme.gray[400],
  };

  if (loading) {
    return (
      <StyledMainContainer>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      </StyledMainContainer>
    );
  }

  if (error) {
    return (
      <StyledMainContainer>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Typography color="error">Error loading sessions</Typography>
        </Box>
      </StyledMainContainer>
    );
  }

  return (
    <StyledMainContainer>
      {/* Agent Status Bar */}
      <StyledAgentStatusBar>
        <Box display="flex" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <StyledStatusDot status={agentStatus} />
            <Select
              value={agentStatus}
              onChange={(e) => setAgentStatus(e.target.value)}
              variant="standard"
              disableUnderline
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: theme.textPrimary,
                "& .MuiSelect-select": { py: 0, pr: 3 },
              }}
            >
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="busy">Busy</MenuItem>
              <MenuItem value="away">Away</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
            </Select>
          </Box>
          <Chip
            label={`${counts.active}/${5} chats`}
            size="small"
            sx={{
              height: "24px",
              fontSize: "12px",
              bgcolor: theme.gray[100],
              color: theme.textSecondary,
            }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => refetch()}
              sx={{ color: theme.textSecondary }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={`${totalUnread} unread messages`}>
            <IconButton
              size="small"
              onClick={(e) => setNotificationAnchorEl(e.currentTarget)}
              sx={{ color: theme.textSecondary }}
            >
              <StyledBadge
                badgeContent={totalUnread}
                invisible={totalUnread === 0}
              >
                <NotificationsIcon fontSize="small" />
              </StyledBadge>
            </IconButton>
          </Tooltip>
        </Box>
      </StyledAgentStatusBar>

      {/* Notification Popover */}
      <Popover
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={() => setNotificationAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <StyledNotificationPopover>
          <Box p={2}>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
              Notifications
            </Typography>
            {counts.pending === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No pending requests
              </Typography>
            ) : (
              pendingSessions
                .filter((s) => s.status === "pending_agent")
                .slice(0, 5)
                .map((session) => (
                  <StyledNotificationItem key={session.id}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: theme.primary }}
                      >
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={500}>
                          Visitor #{(session.userId || "").slice(-6)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(session.createdAt)}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => {
                          handleAccept(session);
                          setNotificationAnchorEl(null);
                        }}
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "12px",
                        }}
                      >
                        Accept
                      </Button>
                    </Box>
                  </StyledNotificationItem>
                ))
            )}
          </Box>
        </StyledNotificationPopover>
      </Popover>

      {/* Main Content */}
      <StyledContentArea>
        {/* Sidebar */}
        <StyledSidebar>
          {/* Filter Tabs */}
          <Box sx={{ px: 1, py: 1, borderBottom: `1px solid ${theme.border}` }}>
            <Box display="flex" gap={0}>
              <StyledTabButton
                active={activeFilter === "all"}
                onClick={() => setActiveFilter("all")}
              >
                All ({counts.all})
              </StyledTabButton>
              <StyledTabButton
                active={activeFilter === "active"}
                onClick={() => setActiveFilter("active")}
              >
                Active ({counts.active})
              </StyledTabButton>
              <StyledTabButton
                active={activeFilter === "pending"}
                onClick={() => setActiveFilter("pending")}
              >
                Pending ({counts.pending})
              </StyledTabButton>
            </Box>
          </Box>

          {/* Session List */}
          <StyledSessionList>
            {filteredSessions.length === 0 ? (
              <Box textAlign="center" py={4} px={2}>
                <ChatBubbleOutlineIcon
                  sx={{ fontSize: 40, color: theme.gray[300], mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  No conversations
                </Typography>
              </Box>
            ) : (
              filteredSessions.map((session) => {
                const isSelected = selectedSession?.id === session.id;
                const unreadCount = unreadMessages.get(session.id) || 0;
                const displayId = (session.userId || "").slice(-6);

                return (
                  <StyledSessionCard
                    key={session.id}
                    active={isSelected}
                    hasUnread={unreadCount > 0}
                    onClick={() => handleSessionSelect(session)}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      width="100%"
                      gap={1.5}
                    >
                      <StyledBadge
                        badgeContent={unreadCount}
                        invisible={unreadCount === 0}
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: isSelected
                              ? theme.primary
                              : theme.gray[200],
                            color: isSelected ? "#fff" : theme.textSecondary,
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      </StyledBadge>
                      <Box flex={1} minWidth={0}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <StyledStatusDot status={session.status} />
                            <Typography
                              variant="body2"
                              fontWeight={unreadCount > 0 ? 600 : 500}
                              noWrap
                            >
                              Visitor #{displayId}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(session.updatedAt || session.createdAt)}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mt={0.5}
                        >
                          <StyledChip status={session.status}>
                            {session.status === "pending_agent"
                              ? "Waiting"
                              : session.status}
                          </StyledChip>
                          {session.botConversationSummary && (
                            <Tooltip title="Has bot conversation">
                              <SmartToyIcon
                                sx={{ fontSize: 14, color: theme.primary }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      {session.status === "ended" && (
                        <Tooltip title="Archive">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              archiveChatSession({
                                variables: { sessionId: session.id },
                                onCompleted: () => {
                                  setPendingSessions((prev) =>
                                    prev.filter((s) => s.id !== session.id)
                                  );
                                  if (selectedSession?.id === session.id) {
                                    setSelectedSession(null);
                                    localStorage.removeItem(
                                      "instaconnect_selected_session_id"
                                    );
                                  }
                                },
                              });
                            }}
                            sx={{ color: theme.textMuted }}
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </StyledSessionCard>
                );
              })
            )}
          </StyledSessionList>

          {/* Admin Tools */}
          <Box px={2} pb={2} pt={1} borderTop={`1px solid ${theme.border}`}>
            <StyledButton
              size="small"
              fullWidth
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={() => {
                if (window.confirm("Clear all chat sessions and messages?")) {
                  clearChatSessions({
                    onCompleted: (data) => {
                      if (data.clearChatSessions.success) {
                        setPendingSessions([]);
                        setSelectedSession(null);
                        setMessages([]);
                        localStorage.removeItem(
                          "instaconnect_selected_session_id"
                        );
                      }
                    },
                  });
                }
              }}
            >
              Clear All
            </StyledButton>
          </Box>
        </StyledSidebar>

        {/* Chat Area */}
        <StyledChatArea>
          {selectedSession ? (
            <>
              {/* Chat Header */}
              <StyledChatHeader>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    sx={{ width: 40, height: 40, bgcolor: theme.primary }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {visitorInfo?.name ||
                          `Visitor #${(selectedSession.userId || "").slice(
                            -6
                          )}`}
                      </Typography>
                      <Tooltip title="Edit visitor info">
                        <IconButton
                          size="small"
                          onClick={() => setEditVisitorDialogOpen(true)}
                          sx={{ color: theme.textMuted, p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {selectedSession.status === "active"
                        ? "Active now"
                        : selectedSession.status}
                    </Typography>
                  </Box>
                </Box>
                {selectedSession.status === "active" && (
                  <StyledActionButtons>
                    <Tooltip title="Audio Call">
                      <StyledActionIconButton
                        onClick={() => startCall("audio")}
                        disabled={videoCallActive}
                      >
                        <MicIcon fontSize="small" />
                      </StyledActionIconButton>
                    </Tooltip>
                    <Tooltip title="Video Call">
                      <StyledActionIconButton
                        onClick={() => startCall("video")}
                        disabled={videoCallActive}
                      >
                        <VideoCallIcon fontSize="small" />
                      </StyledActionIconButton>
                    </Tooltip>
                    <Tooltip title="Co-browse">
                      <StyledActionIconButton
                        onClick={
                          isCoBrowsing
                            ? () => setIsCoBrowsing(false)
                            : handleStartCoBrowsing
                        }
                      >
                        {isCoBrowsing ? (
                          <StopScreenShareIcon fontSize="small" />
                        ) : (
                          <ScreenShareIcon fontSize="small" />
                        )}
                      </StyledActionIconButton>
                    </Tooltip>
                    <Tooltip title="Transfer">
                      <StyledActionIconButton>
                        <SwapHorizIcon fontSize="small" />
                      </StyledActionIconButton>
                    </Tooltip>
                    <Tooltip title="More">
                      <StyledActionIconButton>
                        <MoreVertIcon fontSize="small" />
                      </StyledActionIconButton>
                    </Tooltip>
                    <StyledButton
                      color="error"
                      size="small"
                      onClick={handleEndChat}
                      sx={{ ml: 1 }}
                    >
                      End Chat
                    </StyledButton>
                  </StyledActionButtons>
                )}
              </StyledChatHeader>

              {/* Bot Summary */}
              {selectedSession.botConversationSummary && (
                <Collapse in={showBotSummary}>
                  <StyledBotSummary>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <SmartToyIcon sx={{ fontSize: 16, color: "#0369a1" }} />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="#0369a1"
                        >
                          BOT SUMMARY
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setShowBotSummary(false)}
                      >
                        <ExpandLessIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {selectedSession.botConversationSummary}
                    </Typography>
                  </StyledBotSummary>
                </Collapse>
              )}
              {selectedSession.botConversationSummary && !showBotSummary && (
                <Box
                  sx={{
                    px: 3,
                    py: 1,
                    cursor: "pointer",
                    borderBottom: `1px solid ${theme.border}`,
                  }}
                  onClick={() => setShowBotSummary(true)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <SmartToyIcon sx={{ fontSize: 14, color: "#0369a1" }} />
                    <Typography variant="caption" color="#0369a1">
                      Show bot summary
                    </Typography>
                    <ExpandMoreIcon sx={{ fontSize: 14, color: "#0369a1" }} />
                  </Box>
                </Box>
              )}

              {/* Accept Banner */}
              {(selectedSession.status === "pending" ||
                selectedSession.status === "pending_agent") &&
                !acceptedSessionIds.has(selectedSession.id) &&
                selectedSession.agentId !== currentUserId && (
                  <StyledAcceptBanner>
                    <Typography variant="body2" color="#92400e">
                      This visitor is waiting for assistance
                    </Typography>
                    <Box display="flex" gap={1}>
                      <StyledAcceptButton
                        size="small"
                        onClick={() => handleAccept(selectedSession)}
                      >
                        Accept
                      </StyledAcceptButton>
                      <StyledDeclineButton
                        size="small"
                        onClick={() => handleDecline(selectedSession)}
                      >
                        Decline
                      </StyledDeclineButton>
                    </Box>
                  </StyledAcceptBanner>
                )}

              {/* Messages */}
              <StyledMessagesContainer ref={chatContainerRef}>
                {loadingMessages && messages.length === 0 ? (
                  <Box>
                    <StyledSkeleton sx={{ height: 60, mb: 2 }} />
                    <StyledSkeleton sx={{ height: 60, mb: 2 }} />
                    <StyledSkeleton sx={{ height: 60 }} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    color="text.secondary"
                  >
                    <Typography variant="body2">No messages yet</Typography>
                    <Typography variant="caption">
                      Messages will appear here
                    </Typography>
                  </Box>
                ) : (
                  messages.map((msg, index) => {
                    const isAgent = msg.sender === "agent";
                    const isBot = msg.sender === "bot";
                    const isSystem = msg.sender === "system";
                    const showTime =
                      index === messages.length - 1 ||
                      messages[index + 1]?.sender !== msg.sender;

                    return (
                      <Fade in key={msg.id || index}>
                        <StyledMessageBubble
                          isAgent={isAgent}
                          isBot={isBot}
                          isSystem={isSystem}
                        >
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {msg.content}
                          </Typography>
                          {showTime && (
                            <Typography className="message-time">
                              {formatFullTime(msg.timestamp)}
                            </Typography>
                          )}
                        </StyledMessageBubble>
                      </Fade>
                    );
                  })
                )}
              </StyledMessagesContainer>

              {/* Input Area */}
              {selectedSession.status === "active" && (
                <StyledInputArea>
                  <StyledInputWrapper elevation={0}>
                    <StyledInput
                      placeholder="Type a message..."
                      fullWidth
                      multiline
                      maxRows={4}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <StyledSendButton
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim()}
                    >
                      <SendIcon fontSize="small" />
                    </StyledSendButton>
                  </StyledInputWrapper>
                </StyledInputArea>
              )}

              {/* Ended Session Notice */}
              {selectedSession.status === "ended" && (
                <Box
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderTop: `1px solid ${theme.border}`,
                    bgcolor: theme.gray[50],
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    This conversation has ended
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <StyledEmptyState>
              <ChatBubbleOutlineIcon />
              <h3>Welcome to Support Chat</h3>
              <p>
                Select a conversation from the sidebar to start helping
                customers
              </p>
            </StyledEmptyState>
          )}
        </StyledChatArea>
      </StyledContentArea>

      {/* Video Call Popup */}
      {videoCallActive && (
        <MiroTalkVideoCallPopup
          sessionId={selectedSession?.id}
          callType={callType}
          onEndCall={() => {
            setVideoCallActive(false);
            setCallType(null);
          }}
        />
      )}

      {/* Co-browsing Overlay */}
      {isCoBrowsing && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            bgcolor: "background.paper",
          }}
        >
          <CoBrowsingView
            sessionId={selectedSession?.id}
            onEnd={() => setIsCoBrowsing(false)}
          />
        </Box>
      )}

      {/* Edit Visitor Dialog */}
      <EditVisitorDialog
        open={editVisitorDialogOpen}
        onClose={() => setEditVisitorDialogOpen(false)}
        visitorInfo={visitorInfo}
        sessionId={selectedSession?.visitorSessionId || selectedSession?.userId}
        onSave={(formData) => {
          updateVisitorInfo({
            variables: {
              sessionId:
                selectedSession.visitorSessionId || selectedSession.userId,
              name: formData.name || null,
              email: formData.email || null,
              phone: formData.phone || null,
              company: formData.company || null,
              chatSessionId: selectedSession.id,
            },
          });
        }}
      />
    </StyledMainContainer>
  );
}

// Edit Visitor Dialog Component
function EditVisitorDialog({ open, onClose, visitorInfo, sessionId, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });

  useEffect(() => {
    if (visitorInfo) {
      setFormData({
        name: visitorInfo.name || "",
        email: visitorInfo.email || "",
        phone: visitorInfo.phone || "",
        company: visitorInfo.company || "",
      });
    }
  }, [visitorInfo]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Edit Visitor Information</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, name: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((p) => ({ ...p, email: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) =>
                setFormData((p) => ({ ...p, phone: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company"
              value={formData.company}
              onChange={(e) =>
                setFormData((p) => ({ ...p, company: e.target.value }))
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => onSave(formData)}
          variant="contained"
          color="primary"
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default IncomingRequests;
